import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {useAnalytics} from '@github-ui/use-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback, useMemo} from 'react'
import {FINDINGS_ANALYTICS_EVENTS} from './analytics'
import {
  AUTHOR_IMPLIED_SOURCE_LABELS,
  getFindingSeverity,
  getFindingSourceAuthors,
  getFindingSourceLabels,
  getFindingToolNames,
  isNeedsAttention,
  SEVERITY_ORDER,
} from './entity-helpers'
import {filtersStorageKey} from './storage-keys'
import type {Finding} from './types'

export type FindingsView = 'attention' | 'open' | 'resolved' | 'outdated'
export type SourceFilterType = 'source' | 'source-author'

export interface SourceFilterOption {
  type: SourceFilterType
  key: string
  label: string
  value: string
}

export interface ToolFilterOption {
  key: string
  label: string
  value: string
}

export type FilterFindingsState = ReturnType<typeof useFilterFindings>

const FINDINGS_VIEWS: ReadonlySet<FindingsView> = new Set(['attention', 'open', 'resolved', 'outdated'])
const FINDINGS_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(SEVERITY_ORDER)

interface PersistedFilterFindingsState {
  selectedView: FindingsView
  selectedSeverities: RuleSeverity[]
  selectedSources: string[]
  selectedSourceAuthors: string[]
  selectedTools: string[]
}

const DEFAULT_FILTER_STATE: PersistedFilterFindingsState = {
  selectedView: 'attention',
  selectedSeverities: [],
  selectedSources: [],
  selectedSourceAuthors: [],
  selectedTools: [],
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const uniqueStrings = (values: unknown): string[] => {
  if (!Array.isArray(values)) return []

  const unique = new Set<string>()
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      unique.add(value)
    }
  }
  return [...unique]
}

const normalizeFilterState = (value: unknown): PersistedFilterFindingsState => {
  if (!isRecord(value)) return DEFAULT_FILTER_STATE

  const selectedView = FINDINGS_VIEWS.has(value.selectedView as FindingsView)
    ? (value.selectedView as FindingsView)
    : DEFAULT_FILTER_STATE.selectedView
  const selectedSeverities = uniqueStrings(value.selectedSeverities).filter((severity): severity is RuleSeverity =>
    FINDINGS_SEVERITIES.has(severity as RuleSeverity),
  )
  const selectedSources = uniqueStrings(value.selectedSources)
  const selectedSourceAuthors = uniqueStrings(value.selectedSourceAuthors).map(login => login.toLowerCase())
  const selectedTools = uniqueStrings(value.selectedTools).map(tool => tool.toLowerCase())

  return {selectedView, selectedSeverities, selectedSources, selectedSourceAuthors, selectedTools}
}

const toggleValue = <T extends string>(values: T[], value: T): T[] => {
  const next = new Set(values)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return [...next]
}

const sourceFilterKey = (type: SourceFilterType, value: string): string => `${type}:${value.toLowerCase()}`

const toolFilterKey = (value: string): string => `tool:${value.toLowerCase()}`

export const useFilterFindings = (
  openFindings: Finding[],
  ignoredFindings: Finding[],
  outdatedFindings: Finding[],
  pullRequestBasePath: string,
) => {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const [storedFilterState, setStoredFilterState] = useSessionStorage<PersistedFilterFindingsState>(
    filtersStorageKey(pullRequestBasePath),
    DEFAULT_FILTER_STATE,
  )
  const filterState = useMemo(() => normalizeFilterState(storedFilterState), [storedFilterState])
  const selectedView = filterState.selectedView
  const selectedSeverities = useMemo(() => new Set(filterState.selectedSeverities), [filterState.selectedSeverities])
  const selectedSourceLabels = useMemo(() => new Set(filterState.selectedSources), [filterState.selectedSources])
  const selectedSourceAuthorKeys = useMemo(
    () => new Set(filterState.selectedSourceAuthors.map(login => login.toLowerCase())),
    [filterState.selectedSourceAuthors],
  )
  const selectedToolKeys = useMemo(
    () => new Set(filterState.selectedTools.map(tool => tool.toLowerCase())),
    [filterState.selectedTools],
  )
  const selectedSources = useMemo(
    () =>
      new Set([
        ...filterState.selectedSources.map(source => sourceFilterKey('source', source)),
        ...filterState.selectedSourceAuthors.map(sourceAuthor => sourceFilterKey('source-author', sourceAuthor)),
      ]),
    [filterState.selectedSources, filterState.selectedSourceAuthors],
  )
  const selectedTools = useMemo(
    () => new Set(filterState.selectedTools.map(tool => toolFilterKey(tool))),
    [filterState.selectedTools],
  )

  const allFindings = useMemo(
    () => [...openFindings, ...ignoredFindings, ...outdatedFindings],
    [openFindings, ignoredFindings, outdatedFindings],
  )

  const availableSeverities = useMemo(() => {
    const severities = new Set<RuleSeverity>()
    for (const f of allFindings) {
      const s = getFindingSeverity(f)
      if (s) severities.add(s)
    }
    return SEVERITY_ORDER.filter(s => severities.has(s))
  }, [allFindings])

  const findingsForSelectedView = useMemo(() => {
    if (selectedView === 'resolved') return ignoredFindings
    if (selectedView === 'outdated') return outdatedFindings
    if (selectedView === 'attention') return openFindings.filter(isNeedsAttention)
    return openFindings.filter(f => !isNeedsAttention(f))
  }, [selectedView, openFindings, ignoredFindings, outdatedFindings])

  const availableSources = useMemo(() => {
    const sources = new Set<string>()
    const sourceAuthors = new Map<string, string>()

    for (const f of findingsForSelectedView) {
      for (const label of getFindingSourceLabels(f)) {
        sources.add(label)
      }

      for (const {login} of getFindingSourceAuthors(f)) {
        const key = login.toLowerCase()
        if (AUTHOR_IMPLIED_SOURCE_LABELS.has(key)) continue
        if (!sourceAuthors.has(key)) sourceAuthors.set(key, login)
      }
    }

    const sourceOptions = [...sources].sort().map<SourceFilterOption>(source => ({
      type: 'source',
      key: sourceFilterKey('source', source),
      label: source,
      value: source,
    }))
    // Exclude authors already represented by a source label
    const sourceAuthorOptions = [...sourceAuthors.values()]
      .filter(login => !sources.has(login))
      .sort((a, b) => a.localeCompare(b))
      .map<SourceFilterOption>(sourceAuthor => ({
        type: 'source-author',
        key: sourceFilterKey('source-author', sourceAuthor),
        label: sourceAuthor,
        value: sourceAuthor,
      }))

    return [...sourceOptions, ...sourceAuthorOptions]
  }, [findingsForSelectedView])

  const findingMatchesSelectedSources = useCallback(
    (finding: Finding) => {
      if (selectedSourceLabels.size > 0) {
        const labels = getFindingSourceLabels(finding)
        if (!labels.some(label => selectedSourceLabels.has(label))) return false
      }

      if (selectedSourceAuthorKeys.size > 0) {
        const sourceAuthors = getFindingSourceAuthors(finding)
        if (!sourceAuthors.some(({login}) => selectedSourceAuthorKeys.has(login.toLowerCase()))) return false
      }

      return true
    },
    [selectedSourceLabels, selectedSourceAuthorKeys],
  )

  // Tools reflect the current view and the selected source(s): any finding in the view that
  // matches the selected sources (or every finding, when no source is selected) contributes
  // its tool options.
  const toolsForSelectedSources = useMemo(() => {
    const tools = new Map<string, string>()
    for (const f of findingsForSelectedView) {
      if (!findingMatchesSelectedSources(f)) continue
      for (const tool of getFindingToolNames(f)) {
        const key = tool.toLowerCase()
        if (!tools.has(key)) tools.set(key, tool)
      }
    }

    return [...tools.values()]
      .sort((a, b) => a.localeCompare(b))
      .map<ToolFilterOption>(tool => ({
        key: toolFilterKey(tool),
        label: tool,
        value: tool,
      }))
  }, [findingsForSelectedView, findingMatchesSelectedSources])

  // The Jobs facet is only surfaced when a workflow (source) is selected and more than one job
  // matches it; otherwise it stays hidden and no job filtering is applied.
  const availableTools = useMemo(() => {
    if (selectedSourceLabels.size === 0) return []
    if (toolsForSelectedSources.length <= 1) return []
    return toolsForSelectedSources
  }, [selectedSourceLabels, toolsForSelectedSources])

  const effectiveSelectedToolKeys = useMemo(() => {
    const availableKeys = new Set(availableTools.map(tool => tool.value.toLowerCase()))
    return new Set([...selectedToolKeys].filter(key => availableKeys.has(key)))
  }, [availableTools, selectedToolKeys])

  const updateFilterState = useCallback(
    (update: (current: PersistedFilterFindingsState) => PersistedFilterFindingsState) => {
      setStoredFilterState(prev => update(normalizeFilterState(prev)))
    },
    [setStoredFilterState],
  )

  const handleViewChange = useCallback(
    (view: FindingsView) => {
      updateFilterState(current => ({
        ...current,
        selectedView: view,
        selectedSources: [],
        selectedSourceAuthors: [],
        selectedTools: [],
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'view',
        view,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleSeverityToggle = useCallback(
    (severity: RuleSeverity) => {
      updateFilterState(current => ({
        ...current,
        selectedSeverities: toggleValue(current.selectedSeverities, severity),
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'severity',
        severity,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleSourceToggle = useCallback(
    (sourceFilter: SourceFilterOption) => {
      updateFilterState(current => ({
        ...current,
        selectedSources:
          sourceFilter.type === 'source'
            ? toggleValue(current.selectedSources, sourceFilter.value)
            : current.selectedSources,
        selectedSourceAuthors:
          sourceFilter.type === 'source-author'
            ? toggleValue(current.selectedSourceAuthors, sourceFilter.value.toLowerCase())
            : current.selectedSourceAuthors,
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'source',
        source_type: sourceFilter.type,
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleToolToggle = useCallback(
    (toolFilter: ToolFilterOption) => {
      updateFilterState(current => ({
        ...current,
        selectedTools: toggleValue(current.selectedTools, toolFilter.value.toLowerCase()),
      }))
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
        filter_type: 'tool',
      })
    },
    [updateFilterState, sendAnalyticsEvent],
  )

  const handleClearFilters = useCallback(() => {
    updateFilterState(current => ({
      ...current,
      selectedSeverities: [],
      selectedSources: [],
      selectedSourceAuthors: [],
      selectedTools: [],
    }))
    sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.filterChange.type, FINDINGS_ANALYTICS_EVENTS.filterChange.target, {
      filter_type: 'clear',
    })
  }, [updateFilterState, sendAnalyticsEvent])

  const findingMatchesSelectedFilters = useCallback(
    (finding: Finding) => {
      if (selectedSeverities.size > 0) {
        const severity = getFindingSeverity(finding)
        if (!severity || !selectedSeverities.has(severity)) return false
      }

      if (!findingMatchesSelectedSources(finding)) return false

      if (effectiveSelectedToolKeys.size > 0) {
        const toolNames = getFindingToolNames(finding)
        if (!toolNames.some(tool => effectiveSelectedToolKeys.has(tool.toLowerCase()))) return false
      }

      return true
    },
    [selectedSeverities, findingMatchesSelectedSources, effectiveSelectedToolKeys],
  )

  const displayedFindings = useMemo(
    () => findingsForSelectedView.filter(findingMatchesSelectedFilters),
    [findingsForSelectedView, findingMatchesSelectedFilters],
  )

  const counts = useMemo(() => {
    const attention = openFindings.filter(isNeedsAttention).length
    const other = openFindings.length - attention
    const ignored = ignoredFindings.length
    const outdated = outdatedFindings.length
    const displayed = displayedFindings.length
    const filteredOpenFindings = openFindings.filter(findingMatchesSelectedFilters)
    const filteredAttention = filteredOpenFindings.filter(isNeedsAttention).length
    const filteredOther = filteredOpenFindings.length - filteredAttention
    const filteredIgnored = ignoredFindings.filter(findingMatchesSelectedFilters).length
    const filteredOutdated = outdatedFindings.filter(findingMatchesSelectedFilters).length

    return {
      attentionCount: attention,
      otherCount: other,
      ignoredCount: ignored,
      outdatedCount: outdated,
      displayedCount: displayed,
      filteredAttentionCount: filteredAttention,
      filteredOtherCount: filteredOther,
      filteredIgnoredCount: filteredIgnored,
      filteredOutdatedCount: filteredOutdated,
    }
  }, [openFindings, ignoredFindings, outdatedFindings, displayedFindings, findingMatchesSelectedFilters])

  return {
    selectedView,
    handleViewChange,
    availableSeverities,
    availableSources,
    availableTools,
    selectedSeverities,
    selectedSources,
    selectedTools,
    handleSeverityToggle,
    handleSourceToggle,
    handleToolToggle,
    handleClearFilters,
    displayedFindings,
    ...counts,
  }
}
